import React, { useState, useEffect } from "react";
import { observer } from "mobx-react";
import {
  Card,
  CardHeader,
  CardContent,
  CircularProgress,
  Typography,
  Box,
  List,
  ListItem,
  ListItemButton,
  Button,
  Divider,
} from "@mui/material";
import { WatchLater as ClockIcon, Assignment as TicketIcon, ArrowBack, ArrowForward } from "@mui/icons-material";
import { useQueryClient } from "@tanstack/react-query";
import {
  useExpiredTickets,
  useTicketsByDate,
  useTicketFilter,
  ticketKeys,
  type ExpiredTicketType,
} from "../../stores/Ticketing";
import { UserContext } from "../../stores/User/User.provider";
import useStore from "../../helpers/useStore";
import { TicketsByDateDto, TicketDto, TicketFilterDto } from "../../api-client";
import { getDateLabel, formatDateShort } from "../../helpers/dateFormat";
import UpdateTicketForm from "../Ticketing/TicketEditCreate/updateTicketForm";
import { useLocationId } from "../../stores/User/User.hooks";

const PersonalDigest: React.FC = observer(() => {
  const queryClient = useQueryClient();
  const UserStore = useStore(UserContext);
  const { user } = UserStore;
  const userId = user?.id;
  const locationId = useLocationId();
  
  const [selectedDate, setSelectedDate] = useState(() => {
    const today = new Date();
    return today.toISOString().split('T')[0]; // YYYY-MM-DD format
  });
  const [selectedTicketId, setSelectedTicketId] = useState<number | undefined>();
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  // Now use the proper digestType parameters since userId is available
  const { data: expiredTickets, isLoading: expiredLoading, error: expiredError } = useExpiredTickets({
    digestType: 'personal',
    userId: userId,
    locationId: locationId!,
    enabled: !!userId
  });
  const { data: dateTickets, isLoading: dateLoading, error: dateError } = useTicketsByDate(
    selectedDate,
    locationId!,
    'personal',
    userId,
    { enabled: !!userId }
  );


  const { data: rawTicketFilters } = useTicketFilter();
  const ticketFilters: TicketFilterDto[] = rawTicketFilters ?? [];
  
  const personalExpiredTickets = expiredTickets || [];
  const personalDateTickets = dateTickets || [];

  const navigateDate = (direction: 'prev' | 'next') => {
    const currentDate = new Date(selectedDate);
    currentDate.setDate(currentDate.getDate() + (direction === 'next' ? 1 : -1));
    setSelectedDate(currentDate.toISOString().split('T')[0]);
  };

  // Resolved from the backend's ticketFilters, exactly like getTicketStatusLabel
  // below and the ticket editor. The local map this replaced had drifted from those
  // translations ("Rx Änderung" vs "Rezeptänderung") and was missing WIEDERVORLAGE
  // and BILLING entirely, so those fell through to the raw enum key.
  const getTicketTypeLabel = (ticketType: string) => {
    const typeEntry = ticketFilters.find((filter) => filter.ticketType?.value === ticketType);
    return typeEntry?.ticketType?.label || ticketType;
  };

  const getTicketStatusLabel = (ticketStatus: string) => {
    for (const filter of ticketFilters) {
      const statusObj = filter.statuses?.find(s => s.value === ticketStatus);
      if (statusObj) {
        return statusObj.label;
      }
    }
    return ticketStatus;
  };
  

  const getTicketIcon = (ticket: ExpiredTicketType | TicketsByDateDto, isExpired = false) => {
    const iconColor = isExpired ? '#d32f2f' : '#1976d2';
    
    if ('expiredReminder' in ticket) {
      // Expired ticket logic
      if ((ticket as any).deadline) {
        return <TicketIcon sx={{ fontSize: 18, color: iconColor }} />;
      } else {
        return <ClockIcon sx={{ fontSize: 18, color: iconColor }} />;
      }
    } else if ('matchType' in ticket) {
      // Date ticket logic
      const dateTicket = ticket as TicketsByDateDto;
      if (dateTicket.matchType === 'reminder') {
        return <ClockIcon sx={{ fontSize: 18, color: iconColor }} />;
      } else {
        return <TicketIcon sx={{ fontSize: 18, color: iconColor }} />;
      }
    } else {
      return <TicketIcon sx={{ fontSize: 18, color: iconColor }} />;
    }
  };

  const handleTicketClick = (ticket: ExpiredTicketType | TicketsByDateDto) => {
    setSelectedTicketId(ticket.id);
    setIsDialogOpen(true);
  };

  const handleDialogClose = () => {
    queryClient.invalidateQueries({ queryKey: ticketKeys.expiredTickets.byDigestType('personal', userId, locationId) });
    queryClient.invalidateQueries({ queryKey: ticketKeys.ticketsByDate.byDateAndDigestType(selectedDate, 'personal', userId, locationId) });
    
    setIsDialogOpen(false);
    setSelectedTicketId(undefined);
  };

  const renderTicketItem = (ticket: ExpiredTicketType | TicketsByDateDto, isExpired = false) => {
    const getDescription = () => {
      if ('displayMessage' in ticket) {
        // Expired ticket
        return ticket.displayMessage || 
               ('expiredReminder' in ticket && (ticket.expiredReminder as any)?.alternativeMessage) || 
               "Keine Beschreibung";
      } else {
        // Date ticket
        return (ticket as TicketsByDateDto).description || "Keine Beschreibung";
      }
    };

    return (
      <ListItem key={ticket.id} disablePadding>
        <ListItemButton
          onClick={() => handleTicketClick(ticket)}
          sx={{
            display: "flex",
            alignItems: "flex-start",
            background: "white",
            borderRadius: 1,
            border: "1px solid #e5e5e5",
            boxShadow: "0px 1px 2px rgba(0,0,0,0.06)",
            p: 1.25,
            m: 0.5,
            "&:hover": {
              backgroundColor: "#f9f9f9",
            },
          }}
        >
          {/* LEFT: Icon */}
          <Box sx={{ width: 20, height: 20, mr: 1.5, mt: 0.25, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {getTicketIcon(ticket, isExpired)}
          </Box>
          
          {/* RIGHT: Content */}
          <Box sx={{ flex: 1 }}>
            {/* Line 1: Ticket Type and Patient Name */}
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.25}>
              <Typography variant="body2" fontWeight="bold" sx={{ fontSize: '0.85rem' }}>
                {getTicketTypeLabel(ticket.ticketType)}
              </Typography>
              {ticket.patient &&
                typeof ticket.patient === "object" &&
                "firstName" in ticket.patient &&
                "lastName" in ticket.patient && (
                  <Typography variant="caption" fontWeight={600} color="text.primary" sx={{ fontSize: '0.8rem', textDecoration: 'underline' }}>
                    {(ticket.patient as any).firstName} {(ticket.patient as any).lastName}
                  </Typography>
                )}
            </Box>
            
            {/* Line 2: Creation Date and Status */}
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.25}>
              <Typography fontWeight="bold" variant="caption" color="text.primary" sx={{ fontSize: '0.7rem' }}>
                erstellt am {formatDateShort(ticket.createdAt)}
              </Typography>
              <Typography fontWeight="bold" variant="caption" color="text.primary" sx={{ fontSize: '0.75rem' }}>
                Status: {getTicketStatusLabel(ticket.ticketStatus)}
              </Typography>
            </Box>
            
            {/* Line 3: Description and Last Comment Block */}
            <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={1}>
              {/* Left Side: Description */}
              <Box sx={{ flex: 1, maxWidth: '45%' }}>
                <Typography variant="caption" sx={{ color: "text.secondary", lineHeight: 1.3, fontSize: '0.8rem', display: 'block', wordBreak: 'break-word', whiteSpace: 'normal' }}>
                  {getDescription()}
                </Typography>
              </Box>
              
              {/* Right Side: Last Comment */}
              <Box sx={{ textAlign: 'right', flex: 1, maxWidth: '55%' }}>
                {ticket.lastComment && (
                  <Box>
                    <Typography variant="caption" fontWeight="bold" sx={{ fontSize: '0.7rem', display: 'block', mb: 0.1 }}>
                      Letzte Nachricht:
                    </Typography>
                    <Typography 
                      variant="caption" 
                      color="text.secondary" 
                      sx={{ 
                        fontStyle: "italic", 
                        lineHeight: 1.2, 
                        fontSize: '0.7rem', 
                        display: 'block',
                        wordBreak: 'break-word',
                        whiteSpace: 'normal'
                      }}
                    >
                      {ticket.lastComment}
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>
          </Box>
        </ListItemButton>
      </ListItem>
    );
  };

  return (
    <Card sx={{ minHeight: "440px", maxHeight: "440px", display: "flex", flexDirection: "column" }}>
      <CardHeader
        title="Persönliche Tagestickets"
        sx={{ pl: 3 }}
      />

      <CardContent
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          padding: 1,
        }}
      >

        {/* Simple Date Navigator */}
        <Box mb={1} px={1} display="flex" justifyContent="center" alignItems="center" gap={2}>
          <Button
            size="small"
            variant="text"
            onClick={() => navigateDate('prev')}
            sx={{ minWidth: '40px' }}
          >
            <ArrowBack sx={{ fontSize: 18 }}  />
          </Button>
          
          <Typography variant="body2" fontWeight="bold" sx={{ minWidth: '80px', textAlign: 'center' }}>
            {getDateLabel(selectedDate)}
          </Typography>
          
          <Button
            size="small"
            variant="text"
            onClick={() => navigateDate('next')}
            sx={{ minWidth: '40px' }}
          >
            <ArrowForward sx={{ fontSize: 18 }} />
          </Button>
        </Box>

        {/* Expired Tickets Section */}
        {personalExpiredTickets.length > 0 && (
          <Box mb={2}>
            <List disablePadding>
              {personalExpiredTickets.map((ticket) => renderTicketItem(ticket, true))}
            </List>
            <Divider sx={{ my: 2 }} />
          </Box>
        )}

        {/* Date-specific Tickets Section */}
        <Box>
          {!userId && (
            <Box display="flex" justifyContent="center" alignItems="center" py={4}>
              <CircularProgress size={24} />
              <Typography variant="body2" sx={{ ml: 2 }}>
                Lade Benutzerdaten...
              </Typography>
            </Box>
          )}

          {userId && (expiredLoading || dateLoading) && (
            <Box display="flex" justifyContent="center" alignItems="center" py={4}>
              <CircularProgress size={24} />
            </Box>
          )}

          {userId && (expiredError || dateError) && (
            <Box p={2}>
              <Typography color="error" variant="body2">
                Fehler beim Laden der Tickets
              </Typography>
            </Box>
          )}

          {userId && !dateLoading && !dateError && personalDateTickets.length === 0 && personalExpiredTickets.length === 0 && (
            <Box p={2}>
              <Typography variant="body2" color="text.secondary">
                Keine Tickets für {getDateLabel(selectedDate)}
              </Typography>
            </Box>
          )}

          {userId && !dateLoading && !dateError && personalDateTickets.length > 0 && (
            <List disablePadding>
              {personalDateTickets.map((ticket) => renderTicketItem(ticket, false))}
            </List>
          )}
        </Box>
      </CardContent>
      
      {/* Ticket Update Dialog */}
      <UpdateTicketForm
        key={selectedTicketId ?? 'no-ticket'}
        ticketId={selectedTicketId}
        openTicket={isDialogOpen}
        onCloseTicketForm={handleDialogClose}
      />
    </Card>
  );
});

export default PersonalDigest;